import { useMemo } from "react";

interface PriceDisplayProps {
  price: string;
  discountedPrice?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const PriceDisplay = ({ price, discountedPrice, size = "md", className = "" }: PriceDisplayProps) => {
  const discountPercent = useMemo(() => {
    if (!discountedPrice || !price) return null;
    const orig = Number(price);
    const disc = Number(discountedPrice);
    if (isNaN(orig) || isNaN(disc) || orig <= 0 || disc >= orig) return null;
    return Math.round((1 - disc / orig) * 100);
  }, [price, discountedPrice]);

  const hasDiscount = discountPercent !== null && discountedPrice;

  const sizeClasses = {
    sm: { main: "text-sm font-bold", original: "text-xs", badge: "text-[10px] px-1.5 py-0.5" },
    md: { main: "text-base font-bold", original: "text-xs", badge: "text-xs px-2 py-0.5" },
    lg: { main: "text-xl font-bold", original: "text-sm", badge: "text-xs px-2 py-1" },
  };

  const cls = sizeClasses[size];

  if (!hasDiscount) {
    return (
      <span className={`${cls.main} text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] ${className}`}>
        ₹{price}
      </span>
    );
  }

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <span className={`${cls.main} text-transparent bg-clip-text bg-gradient-to-r from-[#e05a8a] to-[#c0392b]`}>
        ₹{discountedPrice}
      </span>
      <span className={`${cls.original} text-gray-400 line-through font-normal`}>
        ₹{price}
      </span>
      <span className={`${cls.badge} rounded-full bg-green-100 text-green-700 font-semibold whitespace-nowrap`}>
        {discountPercent}% off
      </span>
    </div>
  );
};

export default PriceDisplay;
